/* ================================================================
   DOPAMEAN — Inline menu sections
   Markup: snippets/header-drawer.liquid (details[data-dpm-menu-inline])
   Styles: assets/dopamean-menu-inline.css

   Phones and tablets: every menu item with sub-links (Shop By Style /
   Brand / Clothing / Price) opens its pills right under the tapped item
   instead of sliding in a separate screen, one item at a time.

   Desktop: the menu is a panel under the header with every item with
   sub-links as its own column, so all of them are open whenever the
   panel is, and their names are column headings rather than toggles.

   The theme's MenuDrawer (global.js) listens for clicks on every
   <summary> in the drawer and turns them into slide-in screens. A
   capture-phase listener on the document runs before those, so for the
   marked items we stop the click there and let the browser's own
   <details> open/close do the work (or, on desktop, nothing).
   ================================================================ */
(() => {
  if (window.dpmMenuInlineReady) return;
  window.dpmMenuInlineReady = true;

  const SECTION = 'details[data-dpm-menu-inline]';
  const compact = window.matchMedia('(max-width: 989px)');

  const drawer = () => document.getElementById('Details-menu-drawer-container');
  const summaryOf = (details) => details.querySelector(':scope > summary');

  const scrollParent = (el) => {
    for (let node = el.parentElement; node; node = node.parentElement) {
      const { overflowY } = getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
    }
    return null;
  };

  const closeOthers = (keep) => {
    document.querySelectorAll(`${SECTION}[open]`).forEach((other) => {
      if (other !== keep) other.open = false;
    });
  };

  // Desktop: open every section while the panel is open. The theme
  // closes them all again when the panel closes.
  const openAll = () => {
    const main = drawer();
    if (compact.matches || !main || !main.open) return;
    document.querySelectorAll(SECTION).forEach((details) => {
      details.open = true;
    });
  };

  // Desktop headings aren't controls, so keep them out of the tab order.
  const setHeadingFocus = () => {
    document.querySelectorAll(SECTION).forEach((details) => {
      const summary = summaryOf(details);
      if (!summary) return;
      if (compact.matches) summary.removeAttribute('tabindex');
      else summary.setAttribute('tabindex', '-1');
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const summary = event.target.closest('summary');
      const details = summary && summary.parentElement;
      if (!details || !details.matches(SECTION)) return;

      // Keep the theme's slide-in handler from running. The browser still
      // toggles the <details> itself (stopPropagation never cancels that).
      event.stopPropagation();

      // Desktop: sections stay open, so cancel the toggle too.
      if (!compact.matches) {
        event.preventDefault();
        return;
      }

      if (details.open) return; // closing: nothing else to do

      // Opening: close any other open section first, in the same frame,
      // so two sections never show at once. If that section sat above
      // this one, its pills disappearing would pull this item upwards,
      // so scroll the menu back by the same amount to keep it in place.
      const before = summary.getBoundingClientRect().top;
      closeOthers(details);
      const shift = summary.getBoundingClientRect().top - before;
      if (shift !== 0) {
        const scroller = scrollParent(summary);
        if (scroller) scroller.scrollTop += shift;
      }
    },
    true
  );

  // Desktop: Escape inside a section closes the whole panel. The theme
  // would otherwise close just that section, leaving an empty column.
  document.addEventListener(
    'keyup',
    (event) => {
      if (compact.matches || event.code !== 'Escape' || !event.target.closest(SECTION)) return;
      const main = drawer();
      const mainSummary = main && main.open && summaryOf(main);
      if (!mainSummary) return;
      event.stopPropagation();
      mainSummary.click();
    },
    true
  );

  // Keep aria-expanded in step with the open state, and open the desktop
  // columns along with the panel ('toggle' doesn't bubble, so listen in
  // the capture phase).
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details === drawer()) {
        openAll();
        return;
      }
      if (!details.matches(SECTION)) return;
      const summary = summaryOf(details);
      if (summary) summary.setAttribute('aria-expanded', String(details.open));
    },
    true
  );

  // Crossing the desktop breakpoint with the menu open: reset these
  // sections so an inline-open item doesn't turn into a half-open
  // slide-in screen, then reopen them all if we're now on desktop.
  const reset = () => {
    document.querySelectorAll(SECTION).forEach((details) => {
      if (details.classList.contains('menu-opening')) {
        // It was open as a slide-in screen: undo what the theme did to
        // hide the main list behind it.
        const hiddenList = details.closest('.submenu-open');
        if (hiddenList) hiddenList.classList.remove('submenu-open');
        details.classList.remove('menu-opening');
      }
      details.open = false;
      const summary = summaryOf(details);
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });
    setHeadingFocus();
    openAll();
  };
  if (compact.addEventListener) compact.addEventListener('change', reset);
  else if (compact.addListener) compact.addListener(reset);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setHeadingFocus);
  else setHeadingFocus();
})();
